import React, { useState } from 'react';
import styled from '@emotion/native';
import { FlashList } from '@shopify/flash-list';
import { useMutation, useQuery } from '@apollo/client';
import { BodyText } from '@/shared/components/StyledText';
import { white, mint, green, subheadline } from '@/shared/styles/colors';
import { GET_CLIENT_NOTES } from '@/shared/queries';
import { DELETE_CLIENT_NOTE } from '@/shared/mutations';
import {
  QueryResult,
  QueryClientNotesArgs,
  MutationResult,
  MutationDeleteClientNoteArgs,
  ClientNote,
} from '@/shared/types/generated';
import NoteListItem from './NoteListItem';
import useStore from '@/stores/useStore';
import SentryService from '@/services/sentry/Sentry.service';
import { ActivityIndicator } from 'react-native-paper';
import useIsDesktop from '@/shared/hooks/useIsDesktop';

interface NotesListProps {
  clientId: number;
}

const NotesList: React.FC<NotesListProps> = ({ clientId }) => {
  const isDesktop = useIsDesktop();
  const [removingNoteIds, setRemovingNoteIds] = useState<Set<number>>(
    new Set(),
  );
  const { setSnackbarState } = useStore();

  const { data, loading, error } = useQuery<
    QueryResult<'clientNotes'>,
    QueryClientNotesArgs
  >(GET_CLIENT_NOTES, {
    variables: {
      input: {
        clientId,
      },
    },
    onError: (error) => {
      SentryService.captureError('Error fetching client notes:', error);
    },
  });

  const notes = data?.clientNotes || [];

  const [deleteClientNote] = useMutation<
    MutationResult<'deleteClientNote'>,
    MutationDeleteClientNoteArgs
  >(DELETE_CLIENT_NOTE, {
    refetchQueries: () => [
      {
        query: GET_CLIENT_NOTES,
        variables: { input: { clientId } },
      },
    ],
  });

  const handleRemoveNote = async (noteId: number) => {
    setRemovingNoteIds((prev) => new Set(prev).add(noteId));

    try {
      await deleteClientNote({
        variables: {
          input: {
            id: noteId,
          },
        },
      });

      setSnackbarState({
        open: true,
        message: 'Note removed successfully!',
        severity: 'success',
      });
    } catch (error) {
      SentryService.captureError('Error removing client note:', error);
      setSnackbarState({
        open: true,
        message: 'Failed to remove note. Please try again.',
        severity: 'error',
      });
    } finally {
      setRemovingNoteIds((prev) => {
        const newSet = new Set(prev);
        newSet.delete(noteId);
        return newSet;
      });
    }
  };

  const renderNote = ({ item }: { item: ClientNote }) => (
    <NoteListItem
      note={item}
      onRemove={handleRemoveNote}
      isRemoving={removingNoteIds.has(item.id)}
    />
  );

  if (loading && notes.length === 0) {
    return (
      <LoadingContainer>
        <ActivityIndicator />
      </LoadingContainer>
    );
  }

  if (error && notes.length === 0) {
    return (
      <ErrorContainer>
        <ErrorText>Failed to load notes. Please try again.</ErrorText>
      </ErrorContainer>
    );
  }

  if (notes.length === 0) {
    return (
      <EmptyContainer isDesktop={isDesktop}>
        <EmptyText>
          No notes added yet. Use this area to record this client&apos;s
          preferences, insights, or follow-up ideas from previous sessions.
        </EmptyText>
      </EmptyContainer>
    );
  }

  return (
    <Container>
      <FlashList
        data={notes as ClientNote[]}
        renderItem={renderNote}
        estimatedItemSize={120}
        showsVerticalScrollIndicator={false}
        contentContainerStyle={flashListContentStyle}
      />
    </Container>
  );
};

export default NotesList;

const Container = styled.View({
  flex: 1,
  width: '100%',
  minHeight: 200,
});

const flashListContentStyle = {
  paddingBottom: 20,
};

const LoadingContainer = styled.View({
  backgroundColor: white,
  borderRadius: 8,
  padding: 40,
  borderWidth: 1,
  borderColor: mint,
  alignItems: 'center',
});

const ErrorContainer = styled.View({
  backgroundColor: white,
  borderRadius: 8,
  padding: 40,
  borderWidth: 1,
  borderColor: mint,
  alignItems: 'center',
});

const ErrorText = styled(BodyText)({
  color: subheadline,
  fontSize: 14,
  textAlign: 'center',
});

const EmptyContainer = styled.View<{ isDesktop: boolean }>(({ isDesktop }) => ({
  height: isDesktop ? 140 : 224,
  backgroundColor: mint,
  borderRadius: 8,
  paddingHorizontal: 36,
  alignItems: 'center',
  justifyContent: 'center',
}));

const EmptyText = styled(BodyText)({
  color: green,
  fontSize: 14,
  fontWeight: '400',
  textAlign: 'center',
  maxWidth: 450,
});
